import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  CalendarPlus,
  CalendarBlank,
  Percent,
  Trophy,
  CurrencyEur,
  Info,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import { Skeleton } from 'components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';

interface HighlightUserBreakdown {
  userId: string;
  displayName: string;
  avatarUrl: string | null;
  count?: number;
  totalVolume?: number;
}

interface AcquisitionHighlights {
  currentYear: number;
  dossiersLast30Days: number;
  newDossiersByUser: HighlightUserBreakdown[];
  dossiersThisYear: number;
  dossiersByUser: HighlightUserBreakdown[];
  dealsClosedThisYear: number;
  dealVolumeThisYear: number;
  avgBidYieldThisYear: number;
  avgYieldAllDossiersThisYear: number;
  avgVolumePerDossierThisYear: number;
  totalVolumeThisYear: number;
  volumeByUser: HighlightUserBreakdown[];
  avgVolumeSampleSize: number;
  yieldSampleSize: number;
  allYieldSampleSize: number;
}

const formatMeur = (eur: number): string => {
  if (eur <= 0) return '€ 0';
  const meur = eur / 1_000_000;
  if (meur >= 10) return `€ ${meur.toFixed(1)} M`;
  return `€ ${meur.toFixed(2)} M`;
};

interface PillProps {
  label: string;
  value: ReactNode;
  icon: ReactNode;
  tooltip: ReactNode;
  emphasis?: 'moss' | 'brick' | 'neutral';
  /** Optional content rendered on the right side (e.g., user avatars). */
  rightContent?: ReactNode;
  /** Optional small line under the main value (e.g., a secondary metric). */
  subValue?: ReactNode;
}

const emphasisClasses: Record<NonNullable<PillProps['emphasis']>, string> = {
  moss: 'text-moss-primary',
  brick: 'text-brick-primary',
  neutral: 'text-neutral-primary',
};

const Pill = ({
  label,
  value,
  icon,
  tooltip,
  emphasis = 'neutral',
  rightContent,
  subValue,
}: PillProps) => {
  // Pills with a per-user breakdown need more width to fit avatars + label.
  const widthClass = rightContent
    ? 'min-w-[260px] flex-[1_1_260px]'
    : 'min-w-[180px] flex-[1_1_180px]';
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border border-neutral-100 bg-white px-3 py-2.5 shadow-sm ${widthClass}`}
    >
      <div className={`shrink-0 ${emphasisClasses[emphasis]}`}>{icon}</div>
      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-neutral-secondary-mid">
          <span className="whitespace-nowrap">{label}</span>
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="Hoe wordt deze metric berekend"
                  className="inline-flex shrink-0 items-center text-neutral-secondary-mid hover:text-neutral-primary"
                >
                  <Info className="h-3 w-3" weight="duotone" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs leading-relaxed">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <span
          className={`text-lg font-semibold whitespace-nowrap ${emphasisClasses[emphasis]}`}
        >
          {value}
        </span>
        {subValue && (
          <span className="text-[11px] font-medium leading-tight text-neutral-secondary-mid whitespace-nowrap">
            {subValue}
          </span>
        )}
      </div>
      {rightContent && <div className="shrink-0 ml-1">{rightContent}</div>}
    </div>
  );
};

const initials = (name: string): string =>
  name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();

interface UserBreakdownProps {
  users: HighlightUserBreakdown[];
  /** Extract numeric value from a row (defaults to row.count). */
  getValue?: (u: HighlightUserBreakdown) => number;
  /** Format the displayed value under the avatar (defaults to nl-BE integer). */
  formatValue?: (v: number) => string;
  /** Tooltip suffix describing the unit ("dossiers", "MEUR", ...). */
  unitLabel?: (v: number) => string;
  /** Max avatars rendered before "+N" overflow chip. */
  maxVisible?: number;
}

/**
 * Subtle horizontal stack of user avatars with a per-user value below.
 * Generic — works for counts (dossiers) or formatted currency (MEUR).
 */
const UserBreakdown = ({
  users,
  getValue = (u) => u.count ?? 0,
  formatValue = (v) => v.toLocaleString('nl-BE'),
  unitLabel = (v) => `${v} dossier${v === 1 ? '' : 's'}`,
  maxVisible = 4,
}: UserBreakdownProps) => {
  if (!users || users.length === 0) return null;
  const sorted = [...users].sort((a, b) => getValue(b) - getValue(a));
  const visible = sorted.slice(0, maxVisible);
  const overflow = sorted.length - visible.length;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-end gap-1.5">
        {visible.map((u) => {
          const v = getValue(u);
          return (
            <Tooltip key={u.userId}>
              <TooltipTrigger asChild>
                <div className="flex flex-col items-center gap-0.5">
                  <Avatar className="h-6 w-6 ring-1 ring-white">
                    {u.avatarUrl && (
                      <AvatarImage src={u.avatarUrl} alt={u.displayName} />
                    )}
                    <AvatarFallback className="text-[9px]">
                      {initials(u.displayName)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-[10px] font-medium leading-none text-neutral-secondary-mid">
                    {formatValue(v)}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                <p className="font-medium">{u.displayName}</p>
                <p className="text-neutral-secondary-mid">{unitLabel(v)}</p>
              </TooltipContent>
            </Tooltip>
          );
        })}
        {overflow > 0 && (
          <div className="flex flex-col items-center gap-0.5">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-100 text-[10px] font-medium text-neutral-secondary-mid ring-1 ring-white">
              +{overflow}
            </div>
            <span className="text-[10px] leading-none text-neutral-secondary-mid">
              &nbsp;
            </span>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

const formatMeurCompact = (eur: number): string => {
  if (eur <= 0) return '€0';
  const meur = eur / 1_000_000;
  if (meur >= 10) return `€${meur.toFixed(0)}M`;
  if (meur >= 1) return `€${meur.toFixed(1)}M`;
  // Below €1M, show as €K
  const keur = eur / 1_000;
  return `€${Math.round(keur)}K`;
};

export const AcquisitionsHighlights = () => {
  const { session } = useSession();
  const [data, setData] = useState<AcquisitionHighlights | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/acquisitions/highlights`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        },
        session,
      );
      if (!res.ok) throw new Error(`Highlights ${res.status}`);
      setData(await res.json());
    } catch (e) {
      console.error('[AcquisitionsHighlights] failed:', e);
      toast.error('Kon acquisitie highlights niet laden');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex flex-wrap gap-2.5">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-[58px] flex-1 min-w-[180px]" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const dealYieldPct = (data.avgBidYieldThisYear * 100).toFixed(2);
  const allYieldPct = (data.avgYieldAllDossiersThisYear * 100).toFixed(2);
  const year = data.currentYear;

  return (
    <div className="flex flex-wrap gap-2.5">
      <Pill
        label="Nieuw (30d)"
        value={data.dossiersLast30Days.toLocaleString('nl-BE')}
        icon={<CalendarPlus weight="duotone" className="h-5 w-5" />}
        emphasis="moss"
        rightContent={<UserBreakdown users={data.newDossiersByUser} />}
        tooltip={
          <div className="space-y-1">
            <p>
              Aantal dossiers aangemaakt in de laatste 30 dagen (op basis van{' '}
              <code>createdAt</code>).
            </p>
            <p>
              Avatars rechts tonen wie als verantwoordelijke gekoppeld is aan
              elk nieuw dossier en hoeveel per persoon.
            </p>
          </div>
        }
      />
      <Pill
        label={`Dossiers ${year}`}
        value={data.dossiersThisYear.toLocaleString('nl-BE')}
        icon={<CalendarBlank weight="duotone" className="h-5 w-5" />}
        rightContent={
          <UserBreakdown
            users={data.dossiersByUser}
            unitLabel={(v) =>
              `${v} dossier${v === 1 ? '' : 's'} aangebracht in ${year}`
            }
          />
        }
        tooltip={
          <div className="space-y-1">
            <p>
              Aantal dossiers met <code>createdAt</code> in {year} (van 1 januari
              tot vandaag).
            </p>
            <p>
              Avatars rechts tonen wie het meest aanbrengt — telling per
              verantwoordelijke.
            </p>
          </div>
        }
      />
      <Pill
        label={`Gewonnen ${year}`}
        value={data.dealsClosedThisYear.toLocaleString('nl-BE')}
        subValue={
          data.dealVolumeThisYear > 0
            ? `${formatMeur(data.dealVolumeThisYear)} biedvolume`
            : undefined
        }
        icon={<Trophy weight="duotone" className="h-5 w-5" />}
        emphasis="moss"
        tooltip={
          <div className="space-y-1">
            <p>
              Aantal dossiers in fase <strong>"Deal!"</strong> die in {year} zijn
              aangemaakt.
            </p>
            <p>
              Subregel toont totaal biedvolume: Σ{' '}
              <code>(totalBid || bidPriceActual)</code> over die deals.
            </p>
          </div>
        }
      />
      <Pill
        label={`Gem. yield deals ${year}`}
        value={data.yieldSampleSize > 0 ? `${dealYieldPct}%` : '—'}
        icon={<Percent weight="duotone" className="h-5 w-5" />}
        emphasis="moss"
        tooltip={
          <div className="space-y-1">
            <p>
              Gemiddelde <code>bidPriceActualNetYield</code> over gewonnen deals
              uit {year}.
            </p>
            <p>
              <strong>Formule:</strong> Σ(yield<sub>i</sub>) / aantal deals met
              ingevulde yield
            </p>
            <p>Gebaseerd op {data.yieldSampleSize} deals.</p>
          </div>
        }
      />
      <Pill
        label={`Gem. yield alle ${year}`}
        value={data.allYieldSampleSize > 0 ? `${allYieldPct}%` : '—'}
        icon={<Percent weight="duotone" className="h-5 w-5" />}
        tooltip={
          <div className="space-y-1">
            <p>
              Gemiddelde <code>bidPriceActualNetYield</code> over <em>alle</em>{' '}
              aangebrachte dossiers uit {year} met ingevulde yield. Inclusief
              afgewezen dossiers en open pipeline — niet enkel gewonnen deals.
            </p>
            <p>
              <strong>Formule:</strong> Σ(yield<sub>i</sub>) / aantal dossiers
              met ingevulde yield
            </p>
            <p>Gebaseerd op {data.allYieldSampleSize} dossiers.</p>
          </div>
        }
      />
      <Pill
        label={`Gem. volume ${year}`}
        value={
          data.avgVolumeSampleSize > 0
            ? formatMeur(data.avgVolumePerDossierThisYear)
            : '—'
        }
        icon={<CurrencyEur weight="duotone" className="h-5 w-5" />}
        emphasis="brick"
        tooltip={
          <div className="space-y-1">
            <p>
              Gemiddelde transactiegrootte per dossier (in MEUR) voor dossiers
              uit {year}.
            </p>
            <p>
              <strong>Formule:</strong> gemiddelde van{' '}
              <code>(totalBid || bidPriceActual || grossAskingPrice)</code> over
              dossiers met een ingevuld bedrag.
            </p>
            <p>Gebaseerd op {data.avgVolumeSampleSize} dossiers.</p>
          </div>
        }
      />
      <Pill
        label={`Volume ${year}`}
        value={
          data.totalVolumeThisYear > 0 ? formatMeur(data.totalVolumeThisYear) : '—'
        }
        icon={<CurrencyEur weight="duotone" className="h-5 w-5" />}
        emphasis="brick"
        rightContent={
          <UserBreakdown
            users={data.volumeByUser}
            getValue={(u) => u.totalVolume ?? 0}
            formatValue={formatMeurCompact}
            unitLabel={(v) =>
              `${formatMeur(v)} aangebracht in ${year}`
            }
          />
        }
        tooltip={
          <div className="space-y-1">
            <p>
              Totale transactievolume van alle dossiers met <code>createdAt</code>{' '}
              in {year}.
            </p>
            <p>
              <strong>Formule:</strong> Σ{' '}
              <code>(totalBid || bidPriceActual || grossAskingPrice)</code> over
              dossiers van {year}.
            </p>
            <p>
              Avatars rechts tonen wie hoeveel volume heeft aangebracht (per
              verantwoordelijke).
            </p>
          </div>
        }
      />
    </div>
  );
};
